import React, {useEffect, useState} from 'react';
import {importMediaInterface} from '../../hooks/dynamicImport';
import {ButtonStyledAsAnchor} from '../util/StyledElements';
import styled from 'styled-components';
import {useTscTheme} from '../../context/themeProvider';
import {TscStyles, TscThemeName, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {ITranscript} from '../../model/captionModels';
import {transcriptStore} from '../../context/transcriptProvider';
import {events, ISmartPlayerTimeUpdateEvent} from '@techsmith/techsmith-smartplayer';
import {isMobile} from 'react-device-detect';

const TranscriptItem = styled.div<{isHighlight: boolean; theme: TscThemeNames}>`
   display: flex;
   align-items: center;
   padding: 8px 8px 8px 4px;
   margin: 1px 0;
   border-radius: ${TscStyles.border.radius.lg};
   background-color: ${props => props.isHighlight ? props.theme === TscThemeName.dusk ? TscStyles.color.action.secondaryDusk : TscStyles.color.ui.dawn.lightMedium : 'transparent'};
   border: 1px solid ${props => props.isHighlight ? props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.lightMedium : TscStyles.color.ui.dawn.medium : 'transparent'};
   overflow: auto;
   gap: 8px;
   font-family: Figtree, sans-serif;
   font-weight: 500;
   font-size: 0.8125rem;
   transition: all 0.15s ease-out;

   &:hover {
      cursor: pointer;
   }

   button {
      color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
      background-color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.action.secondaryDusk : TscStyles.color.ui.dawn.lightMedium};
      border-radius: ${TscStyles.border.radius.xs};
      min-width: 48px;
      padding: 0 2px;
      align-self: flex-start;
      text-decoration: none;
      font-feature-settings: "tnum";
      &:hover {
         outline-style: none;
         outline-width: none;
         outline-offset: none;
      }
   }
`;

const TranscriptTextItem = styled.div<{theme: TscThemeNames}>`
   font-family: Figtree, sans-serif;
   font-weight: 500;
   font-size: 0.8125rem;
   line-height: 1rem;
   margin-right: 1rem;
   flex: 1;
   text-decoration: none;
   overflow: hidden;
   word-wrap: break-word;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

const TranscriptWrapper = styled.div<{showSpinner: boolean; showError: boolean}>`
   padding: 8px;
`;

export const TranscriptTabBase: React.FC<IStateMappedProps> = ({transcript}) => {
   const [currentSecond, setCurrentSecond] = useState(null);
   const [hoveredKey, setHoveredKey] = useState(null);
   const theme = useTscTheme();

   const setMediaTime = async (time: string) => {
      const mediaInterface = await importMediaInterface();
      mediaInterface.currentTime = parseInt(time, 10);
   };

   const setTranscriptTime = async () => {
      const mediaInterface = await importMediaInterface();
      const currentTimeFloor = Math.floor(mediaInterface.currentTime);
      setCurrentSecond(currentTimeFloor);
   };

   const initializeTranscript = () => {
      void setTranscriptTime();
   };

   const addEventListener = async () => {
      const mediaInterface = await importMediaInterface();
      mediaInterface.addEventListener<ISmartPlayerTimeUpdateEvent>(events.ExternalVideo.VideoTimeUpdate, setTranscriptTime);
   };

   const removeEventListener = async () => {
      const mediaInterface = await importMediaInterface();
      mediaInterface.removeEventListener(events.ExternalVideo.VideoTimeUpdate, setTranscriptTime);
   };

   useEffect(() => {
      initializeTranscript();
      void addEventListener();
      return () => {
         void removeEventListener();
      };
   }, []);

   return (
      <TranscriptWrapper
         showSpinner={false}
         showError={false}
      >
         {transcript && Object.entries(transcript).map(([key, value]) => {
            const keyInt = parseInt(key, 10);
            return (
               <TranscriptItem
                  onMouseOver={() => !isMobile && setHoveredKey(key)}
                  onMouseOut={() => setHoveredKey(null)}
                  key={key}
                  theme={theme}
                  isHighlight={key === hoveredKey || currentSecond !== null && currentSecond >= keyInt && currentSecond < keyInt + value.duration}
                  onClick={() => setMediaTime(key)}
               >
                  <ButtonStyledAsAnchor>
                     {value.displayTime}
                  </ButtonStyledAsAnchor>
                  <TranscriptTextItem theme={theme}>
                     {value.text}
                  </TranscriptTextItem>
               </TranscriptItem>
            );
         })}
      </TranscriptWrapper>
   );
};

export interface IStateMappedProps {
   transcript: ITranscript;
}

const mapStatesToProps = (
   // eslint-disable-next-line no-empty-pattern
   {},
   {transcript}: Partial<IStateMappedProps>
): IStateMappedProps => ({transcript});

export default withMemoizedContexts(mapStatesToProps, transcriptStore)(TranscriptTabBase);
